import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import Select from 'react-select';
import dayjs from 'dayjs';
// import { useCarManagmentContext } from 'providers/CarManagmentProvider';
import axios from 'axios';

interface QuickTripModalProps {
  show: boolean;
  onHide: () => void;
  matricula: string;
  guidCarro: string;
  maxDuration?: number;
  onSuccess: (tripId: number) => void;
}

type Company = {
  idlocal: number;
  nome_curto: string;
  nome_longo: string;
};

type User = {
  id_user: number;
  name: string;
};

type SelectData = {
  label: string;
  value: number;
};

// const durationOptions = [
//   { label: '+15 min', value: 15 },
//   { label: '+30 min', value: 30 },
//   { label: '+1 hora', value: 60 }
// ];

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/gcViagems',
  // baseURL: 'http://localhost:3400/gcViagems', // testing query
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const QuickTripModal = ({
  show,
  onHide,
  matricula,
  guidCarro,
  maxDuration,
  onSuccess
}: QuickTripModalProps) => {
  //const { createQuickTrip, loading } = useCarManagmentContext;

  // Update duration options based on maxDuration
  const durationOptions = useMemo(() => {
    const allOptions = [
      { label: '+15 min', value: 15 },
      { label: '+30 min', value: 30 },
      { label: '+1 hora', value: 60 }
    ];

    if (typeof maxDuration !== 'number') {
      return allOptions;
    }

    if (maxDuration < 15) {
      return [];
    }

    return allOptions.filter(opt => opt.value <= maxDuration);
  }, [maxDuration]);

  // State for form data
  const [selectedDuration, setSelectedDuration] = useState<{
    label: string;
    value: number;
  }>(durationOptions[0] ?? { label: '+15 min', value: 15 });

  const [selectedUsers, setSelectedUsers] = useState<SelectData[]>([]);
  const [userOptions, setUserOptions] = useState<SelectData[]>([]);

  const [selectedLocations, setSelectedLocations] = useState<SelectData[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectData[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (durationOptions.length === 0) {
      setSelectedDuration({ label: '+15 min', value: 15 });
      return;
    }

    if (!durationOptions.some(opt => opt.value === selectedDuration.value)) {
      setSelectedDuration(durationOptions[0]);
    }
  }, [durationOptions, selectedDuration.value]);

  const getListOfCompanies = async () => {
    try {
      const response = await apiClient.get('/companiesList');

      const companies = response.data.map((company: Company) => ({
        value: company.idlocal,
        label: `${company.nome_curto} - ${company.nome_longo}`
      }));

      setLocationOptions(companies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  // get entity's users list
  const getListOfUsers = async () => {
    try {
      const response = await apiClient.get('/entitiesUsers');
      const users = response.data.map((user: User) => ({
        value: user.id_user,
        label: user.name
      }));
      setUserOptions(users);
    } catch (error) {
      /* empty */
    }
  };

  // Fetch users and locations
  useEffect(() => {
    setSelectedUsers([]);
    setSelectedLocations([]);
    const fetchData = async () => {
      try {
        // Fetch users
        getListOfUsers();
        // Fetch locations
        getListOfCompanies();
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };

    if (show) {
      fetchData();
    }
  }, [show]);

  // Calculate start and end times
  const now = dayjs();
  const startTime = now.format('YYYY-MM-DD HH:mm:ss');
  const endTime = now
    .add(selectedDuration.value, 'minute')
    .format('YYYY-MM-DD HH:mm:ss');

  const handleSubmit = async () => {
    if (!selectedLocations || selectedLocations.length === 0) {
      setError('Please select at least one location');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare payload for quick trip creation
      const payload = {
        guidCarro: guidCarro,
        start: startTime,
        end: endTime,
        users: selectedUsers.map(u => u.value),
        locations: selectedLocations.map(l => l.value)
        // No external passengers, takecar, or leavecar
      };
      const response = await apiClient.post('/saidaRapida', payload);
      onSuccess(response.data.id); // Pass the created trip ID to the onSuccess callback
      onHide();
    } catch (err) {
      console.error('Failed to create quick trip:', err);
      setError('Failed to create quick trip. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleHide = () => {
    setSelectedDuration(durationOptions[0]);
    setSelectedUsers([]);
    setSelectedLocations([]);
    setError(null);
    onHide();
  };

  return (
    <Modal
      className="quicktrip-modal"
      show={show}
      onHide={handleHide}
      contentClassName="border-translucent"
      size="lg"
    >
      <Modal.Header className="px-card border-0">
        <div className="w-100 d-flex justify-content-between align-items-start">
          <div>
            <h5 className="mb-0 lh-sm text-body-highlight">Saída rápida</h5>
            <p className="mb-0 fs-10 text-body-tertiary">Carro: {matricula}</p>
          </div>
          <Button className="p-1 fs-10 text-body" onClick={handleHide}>
            X
          </Button>
        </div>
      </Modal.Header>

      <Form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Modal.Body className="p-card">
          {error && <div className="alert alert-danger mb-3 p-2">{error}</div>}

          {/* Start time - fixed to now, not editable */}
          <Form.Group className="mb-3 display-flex flex-row align-items-center">
            <div>
              Data de saída: {'   '} {now.format('D [de] MMMM [às] HH:mm')}
            </div>
          </Form.Group>

          {/* Duration selection */}
          <Form.Group className="mb-3">
            <Form.Label>Duration</Form.Label>
            {durationOptions.length === 0 ? (
              <div className="alert alert-warning mb-3 p-2">
                Não há tempo suficiente para uma saída rápida antes da próxima
                viagem.
              </div>
            ) : (
              <div className="d-flex gap-2">
                {durationOptions.map(option => (
                  <Button
                    key={option.value}
                    variant={
                      selectedDuration.value === option.value
                        ? 'primary'
                        : 'outline-primary'
                    }
                    onClick={() => setSelectedDuration(option)}
                    className="flex-grow-1"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}
          </Form.Group>

          {/* End time - calculated, not editable */}
          <Form.Group className="mb-3">
            <div>
              Data de fim:{'   '}
              {dayjs()
                .add(selectedDuration.value, 'minute')
                .format('D [de] MMMM [às] HH:mm')}
            </div>
          </Form.Group>

          {/* Locations - from different source */}
          <Form.Group className="mb-3">
            <Form.Label>Locais</Form.Label>
            <Select
              isMulti
              options={locationOptions}
              value={selectedLocations}
              onChange={data => setSelectedLocations([...data])}
              placeholder="Select locations..."
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </Form.Group>

          {/* Passengers */}
          <Form.Group className="mb-3">
            <Form.Label>Passageiros</Form.Label>
            <Select
              isMulti
              options={userOptions}
              value={selectedUsers}
              onChange={data => setSelectedUsers([...data])}
              placeholder="Select passengers..."
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end align-items-center border-0">
          <Button
            variant="primary"
            type="submit"
            disabled={
              isLoading ||
              selectedLocations.length === 0 ||
              durationOptions.length === 0
            }
            className="px-4"
          >
            {isLoading ? (
              <Spinner
                animation="border"
                role="status"
                variant="light"
                size="sm"
              />
            ) : (
              'Start Quick Trip'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default QuickTripModal;
